import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Upload, Trash2, Download, Eye, File, FileImage, ExternalLink, X, Music, Sparkles, Link2, Loader2, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ScheduleNotesReviewSections, ExtraMomentsReviewSection, ReunionDetectedCard, buildOverwriteWarning } from './components/EventScheduleNotes';

const TIPOS_DOCUMENTO = [
  { value: 'contrato', label: '📄 Contrato', icon: FileText },
  { value: 'logo_fotomaton', label: '🖼️ Logo Fotomatón', icon: FileImage },
  { value: 'logo_360', label: '🎬 Logo 360', icon: FileImage },
  { value: 'referencia', label: '💡 Referencia', icon: File },
  { value: 'plano', label: '📐 Plano del Venue', icon: FileText },
  { value: 'otro', label: '📎 Otro', icon: File },
];

export const GestionDocumentos = ({ playlist, onUpdate, API }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState('otro');
  // Preview modal state
  const [previewDoc, setPreviewDoc] = useState(null); // { id, nombre, kind: 'pdf'|'image'|'other', blobUrl }
  const [previewLoading, setPreviewLoading] = useState(false);
  // Extract links state (per-doc loading)
  const [extractingId, setExtractingId] = useState(null);
  // Iter89.12: review modal state
  const [reviewDialog, setReviewDialog] = useState(null); // {doc, candidates, summary, selected: Set<id>}
  const [confirming, setConfirming] = useState(false);
  // Fase 3 (2026-10): re-análisis masivo de horarios/observaciones de TODOS los documentos
  const [reanalyzing, setReanalyzing] = useState(false);

  const isExtractable = (doc) => {
    const name = (doc.nombre || doc.filename || '').toLowerCase();
    const ct = (doc.content_type || '').toLowerCase();
    const isDocType = name.endsWith('.pdf') || name.endsWith('.docx') || name.endsWith('.doc') || ct.includes('pdf') || ct.includes('wordprocessingml') || ct.includes('msword');
    const isImageType = /\.(jpe?g|png|webp|heic|heif|gif)$/.test(name) || ct.startsWith('image/');
    return isDocType || isImageType;
  };

  const handleExtractLinks = async (doc) => {
    const token = localStorage.getItem('token');
    const playlistId = playlist?.id;
    if (!playlistId) return;
    setExtractingId(doc.id);
    // Iter91.9: pattern job + polling para evitar "Body is disturbed or locked"
    // que ocurre en iOS Safari/PWA cuando la fetch dura >30s. El backend hace
    // la extracción LLM en background y nosotros vamos consultando estado.
    const toastId = toast.loading('Analizando documento... esto puede tardar 1-2 minutos', { duration: 6 * 60 * 1000 });
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    try {
      // 1) Iniciar job
      const startRes = await fetch(
        `${API}/playlists/${playlistId}/documentos/${doc.id}/extract-links-async/start?dry_run=true`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!startRes.ok) {
        toast.dismiss(toastId);
        let msg = `Error iniciando análisis (${startRes.status})`;
        try { const j = await startRes.json(); if (j?.detail) msg = j.detail; } catch (_e) { /* noop */ }
        toast.error(msg);
        return;
      }
      let job = {};
      try { job = await startRes.json(); } catch (_e) { job = {}; }
      const jobId = job.job_id;
      if (!jobId) {
        toast.dismiss(toastId);
        toast.error('Respuesta inválida al iniciar análisis');
        return;
      }

      // 2) Polling cada 3s, máximo 6 minutos
      let data = null;
      const maxAttempts = 120; // 120 × 3s = 6 min
      for (let i = 0; i < maxAttempts; i++) {
        await sleep(3000);
        let pollRes;
        try {
          pollRes = await fetch(
            `${API}/playlists/${playlistId}/documentos/${doc.id}/extract-links-async/status/${jobId}`,
            { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (pollErr) {
          // Error de red transitorio: continuar polling
          continue;
        }
        if (!pollRes.ok) {
          toast.dismiss(toastId);
          toast.error(`Error consultando estado (${pollRes.status})`);
          return;
        }
        let status = {};
        try { status = await pollRes.json(); } catch (_e) { continue; }
        if (status.status === 'running') continue;
        if (status.status === 'error') {
          toast.dismiss(toastId);
          toast.error(`Error analizando: ${status.error || 'desconocido'}`, { duration: 8000 });
          return;
        }
        if (status.status === 'done') {
          data = status.result || {};
          break;
        }
      }

      if (!data) {
        toast.dismiss(toastId);
        toast.error('Análisis tardó demasiado (>6 min). Inténtalo de nuevo.', { duration: 8000 });
        return;
      }

      const candidates = data.nuevas_peticiones || [];
      const horarios = data.horarios_detectados || [];
      const observaciones = data.observaciones_detectadas || [];
      const momentosExtra = data.momentos_extra_detectados || [];
      const reunion = data.reunion_detectada || { detectada: false };
      toast.dismiss(toastId);
      if (candidates.length === 0 && horarios.length === 0 && observaciones.length === 0 && momentosExtra.length === 0 && !reunion.detectada) {
        const msg = data.message || 'No se encontraron canciones, horarios ni observaciones extraíbles';
        toast.info(msg);
        return;
      }
      // Abrir modal de revisión con todos seleccionados por defecto
      setReviewDialog({
        doc,
        candidates,
        summary: {
          total_found: data.total_found,
          llm_songs_total: data.llm_songs_total,
          llm_matched_in_spotify: data.llm_matched_in_spotify,
          skipped: data.skipped,
        },
        selected: new Set(candidates.map(c => c.id)),
        horarios,
        selectedHorarios: new Set(horarios.map((_, idx) => idx)),
        observaciones,
        selectedObservaciones: new Set(observaciones.map((_, idx) => idx)),
        momentosExtra,
        selectedMomentosExtra: new Set(momentosExtra.map((_, idx) => idx)),
        reunion,
        reunionEnabled: !!reunion.detectada,
        reunionFecha: reunion.fecha || '',
        reunionHora: reunion.hora || '',
      });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`Error analizando: ${e?.message || String(e)}`, { duration: 8000 });
    } finally {
      setExtractingId(null);
    }
  };
  
  const toggleCandidate = (id) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selected);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, selected: next };
    });
  };
  
  const toggleAllCandidates = (allChecked) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const next = allChecked ? new Set() : new Set(prev.candidates.map(c => c.id));
      return { ...prev, selected: next };
    });
  };

  const toggleHorario = (idx) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selectedHorarios);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...prev, selectedHorarios: next };
    });
  };

  const toggleObservacion = (idx) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selectedObservaciones);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...prev, selectedObservaciones: next };
    });
  };

  const toggleMomentoExtra = (idx) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selectedMomentosExtra);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...prev, selectedMomentosExtra: next };
    });
  };

  const setReunionEnabled = (enabled) => {
    setReviewDialog(prev => (prev ? { ...prev, reunionEnabled: enabled } : prev));
  };
  const setReunionFecha = (fecha) => {
    setReviewDialog(prev => (prev ? { ...prev, reunionFecha: fecha } : prev));
  };
  const setReunionHora = (hora) => {
    setReviewDialog(prev => (prev ? { ...prev, reunionHora: hora } : prev));
  };

  // Fase 3 (2026-10): re-análisis masivo — re-lee TODOS los documentos + notas del
  // evento para extraer horarios/observaciones/momentos extra/reunión (sin tocar canciones).
  // Útil para eventos antiguos subidos antes de que existiera esta extracción.
  const handleReanalyzeAll = async () => {
    const playlistId = playlist?.id;
    if (!playlistId) return;
    setReanalyzing(true);
    const toastId = toast.loading('Re-analizando documentos del evento...', { duration: 2 * 60 * 1000 });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/playlists/${playlistId}/documentos/reanalyze-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = {};
      try { data = await res.json(); } catch (_e) { /* noop */ }
      toast.dismiss(toastId);
      if (!res.ok) {
        toast.error(data.detail || `Error re-analizando (${res.status})`);
        return;
      }
      const horarios = data.horarios_detectados || [];
      const observaciones = data.observaciones_detectadas || [];
      const momentosExtra = data.momentos_extra_detectados || [];
      const reunion = data.reunion_detectada || { detectada: false };
      if (horarios.length === 0 && observaciones.length === 0 && momentosExtra.length === 0 && !reunion.detectada) {
        toast.info(data.message || 'No se detectaron horarios, observaciones ni reuniones nuevas en los documentos.');
        return;
      }
      setReviewDialog({
        doc: null,
        candidates: [],
        summary: {},
        selected: new Set(),
        horarios,
        selectedHorarios: new Set(horarios.map((_, idx) => idx)),
        observaciones,
        selectedObservaciones: new Set(observaciones.map((_, idx) => idx)),
        momentosExtra,
        selectedMomentosExtra: new Set(momentosExtra.map((_, idx) => idx)),
        reunion,
        reunionEnabled: !!reunion.detectada,
        reunionFecha: reunion.fecha || '',
        reunionHora: reunion.hora || '',
      });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`Error: ${e?.message || String(e)}`);
    } finally {
      setReanalyzing(false);
    }
  };
  
  // Iter89.13: editar inline título/artista/momento de una candidata
  const updateCandidateField = (id, field, value) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const nextCandidates = prev.candidates.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      );
      return { ...prev, candidates: nextCandidates };
    });
  };
  
  // Iter89.14: re-buscar en Spotify tras editar título/artista
  // Iter89.15: top-5 con miniatura para casos ambiguos
  const [searchingId, setSearchingId] = useState(null);
  const [spotifyOptions, setSpotifyOptions] = useState({}); // {candidateId: [tracks]}
  
  const reSearchSpotify = async (candidate) => {
    const title = (candidate.titulo || '').trim();
    const artist = (candidate.artista || '').trim();
    if (!title) {
      toast.warning('Escribe un título primero');
      return;
    }
    const query = `${title} ${artist}`.trim();
    setSearchingId(candidate.id);
    try {
      const res = await fetch(
        `${API}/spotify/public/search?query=${encodeURIComponent(query)}`,
        { method: 'GET' }
      );
      // Iter91.8: res.json() con try/catch (evita "Body is disturbed or locked" en iOS PWA)
      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        toast.error(res.ok ? `Respuesta no válida: ${parseErr?.message}` : `Error Spotify (${res.status})`);
        return;
      }
      if (!res.ok) {
        toast.error(data.detail || `Error en Spotify search (${res.status})`);
        return;
      }
      const tracks = (data.tracks || []).slice(0, 5);
      if (tracks.length === 0) {
        toast.info('Spotify no encontró match para esa búsqueda');
        setSpotifyOptions(prev => ({ ...prev, [candidate.id]: [] }));
        return;
      }
      // Mostrar las opciones top-5 inline para que el DJ elija
      setSpotifyOptions(prev => ({ ...prev, [candidate.id]: tracks }));
    } catch (e) {
      toast.error(`Error: ${e.message || e}`);
    } finally {
      setSearchingId(null);
    }
  };
  
  const applySpotifyOption = (candidateId, track) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const nextCandidates = prev.candidates.map(c =>
        c.id === candidateId
          ? {
              ...c,
              titulo: track.name || c.titulo,
              artista: (track.artists && track.artists[0] && track.artists[0].name) || c.artista,
              spotify_url: track.spotify_url || '',
              spotify_uri: track.uri || '',
              url: track.spotify_url || c.url,
              tipo: track.spotify_url ? 'spotify_link' : c.tipo,
            }
          : c
      );
      return { ...prev, candidates: nextCandidates };
    });
    // Cerrar el panel de opciones tras la elección
    setSpotifyOptions(prev => {
      const next = { ...prev };
      delete next[candidateId];
      return next;
    });
  };
  
  const closeSpotifyOptions = (candidateId) => {
    setSpotifyOptions(prev => {
      const next = { ...prev };
      delete next[candidateId];
      return next;
    });
  };
  
  // Iter99: tab activo del modal (separar peticiones con momento vs lista personalizada)
  const [activeTab, setActiveTab] = useState('peticion'); // 'peticion' | 'lista_personalizada'

  // Iter99: cambia el tipo_destino de una candidata (mover entre tabs)
  const swapCandidateDestino = (id) => {
    setReviewDialog(prev => {
      if (!prev) return prev;
      const nextCandidates = prev.candidates.map(c =>
        c.id === id
          ? {
              ...c,
              tipo_destino: c.tipo_destino === 'lista_personalizada' ? 'peticion' : 'lista_personalizada',
              // Si pasa a lista_personalizada limpiar momento (no aplica)
              ...(c.tipo_destino !== 'lista_personalizada' ? { momento: '' } : {}),
            }
          : c
      );
      return { ...prev, candidates: nextCandidates };
    });
  };

  const confirmCreatePeticiones = async () => {
    if (!reviewDialog) return;
    const playlistId = playlist?.id;
    const token = localStorage.getItem('token');
    const selectedItems = reviewDialog.candidates.filter(c => reviewDialog.selected.has(c.id));
    const selectedHorariosList = reviewDialog.horarios.filter((_, idx) => reviewDialog.selectedHorarios.has(idx));
    const selectedObservacionesList = reviewDialog.observaciones.filter((_, idx) => reviewDialog.selectedObservaciones.has(idx));
    const selectedMomentosExtraList = (reviewDialog.momentosExtra || []).filter((_, idx) => reviewDialog.selectedMomentosExtra.has(idx));
    const wantsReunionTask = reviewDialog.reunionEnabled && reviewDialog.reunion?.detectada;
    if (
      selectedItems.length === 0 && selectedHorariosList.length === 0 &&
      selectedObservacionesList.length === 0 && selectedMomentosExtraList.length === 0 && !wantsReunionTask
    ) {
      toast.warning('Selecciona al menos un elemento (canción, horario, observación o reunión)');
      return;
    }
    if (wantsReunionTask && !reviewDialog.reunionFecha) {
      toast.warning('Introduce la fecha de la reunión para crear la tarea');
      return;
    }
    // Aviso antes de sobrescribir horarios que el evento ya tenía
    if (selectedHorariosList.length > 0 || selectedMomentosExtraList.length > 0) {
      const conflicts = buildOverwriteWarning(
        [...selectedHorariosList, ...selectedMomentosExtraList.map(m => ({ momento: m.nombre, hora_inicio: m.hora_inicio, hora_fin: m.hora_fin, es_extra: true }))],
        playlist?.horarios_momentos || []
      );
      if (conflicts.length > 0) {
        const ok = window.confirm(
          `Vas a sobrescribir estos horarios ya guardados:\n\n${conflicts.join('\n')}\n\n¿Confirmas?`
        );
        if (!ok) return;
      }
    }
    // Iter99: separar por tipo_destino para llamar a dos endpoints distintos
    const peticionItems = selectedItems.filter(c => (c.tipo_destino || 'peticion') !== 'lista_personalizada');
    const listaItems = selectedItems.filter(c => c.tipo_destino === 'lista_personalizada');
    setConfirming(true);
    try {
      let createdPeticiones = 0;
      let skippedPeticiones = 0;
      let createdLista = 0;
      let skippedLista = 0;

      // 1) Peticiones (con momento)
      if (peticionItems.length > 0) {
        const res = await fetch(
          `${API}/playlists/${playlistId}/peticiones-batch-add`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ peticiones: peticionItems }),
          }
        );
        let data = {};
        try { data = await res.json(); } catch (_e) { /* noop */ }
        if (!res.ok) {
          toast.error(data.detail || `Error creando peticiones (${res.status})`);
          return;
        }
        createdPeticiones = data.created || 0;
        skippedPeticiones = data.skipped || 0;
      }

      // 2) Lista personalizada (canciones bajo encabezados de género)
      if (listaItems.length > 0) {
        const res2 = await fetch(
          `${API}/playlists/${playlistId}/lista-personalizada-batch-add`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: listaItems }),
          }
        );
        let data2 = {};
        try { data2 = await res2.json(); } catch (_e) { /* noop */ }
        if (!res2.ok) {
          toast.error(data2.detail || `Error añadiendo a lista personalizada (${res2.status})`);
          return;
        }
        createdLista = data2.created || 0;
        skippedLista = data2.skipped || 0;
      }

      // 3) Horarios por momento + momentos extra + observaciones operativas (Fase 1+3)
      let horariosAplicados = 0;
      let observacionesAplicadas = 0;
      if (selectedHorariosList.length > 0 || selectedObservacionesList.length > 0 || selectedMomentosExtraList.length > 0) {
        const res3 = await fetch(
          `${API}/playlists/${playlistId}/schedule-notes-batch-add`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              horarios: selectedHorariosList,
              observaciones: selectedObservacionesList,
              momentos_extra: selectedMomentosExtraList,
            }),
          }
        );
        let data3 = {};
        try { data3 = await res3.json(); } catch (_e) { /* noop */ }
        if (!res3.ok) {
          toast.error(data3.detail || `Error guardando horarios/observaciones (${res3.status})`);
          return;
        }
        horariosAplicados = selectedHorariosList.length + selectedMomentosExtraList.length;
        observacionesAplicadas = data3.added_observaciones || 0;
      }

      // 4) Tarea de reunión previa (Fase 3)
      let reunionCreada = false;
      if (wantsReunionTask) {
        const res4 = await fetch(
          `${API}/playlists/${playlistId}/crear-tarea-reunion`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fecha: reviewDialog.reunionFecha,
              hora: reviewDialog.reunionHora,
              texto: reviewDialog.reunion?.texto || '',
            }),
          }
        );
        let data4 = {};
        try { data4 = await res4.json(); } catch (_e) { /* noop */ }
        if (!res4.ok) {
          toast.error(data4.detail || `Error creando tarea de reunión (${res4.status})`);
        } else {
          reunionCreada = !!data4.created;
        }
      }

      const totalSkipped = skippedPeticiones + skippedLista;
      const parts = [];
      if (createdPeticiones > 0) parts.push(`${createdPeticiones} peticiones`);
      if (createdLista > 0) parts.push(`${createdLista} a lista personalizada`);
      if (horariosAplicados > 0) parts.push(`${horariosAplicados} horarios`);
      if (observacionesAplicadas > 0) parts.push(`${observacionesAplicadas} observaciones`);
      if (reunionCreada) parts.push('tarea de reunión creada');
      const msg = parts.length > 0
        ? `✅ Guardado: ${parts.join(' + ')}${totalSkipped ? ` · ${totalSkipped} duplicadas saltadas` : ''}`
        : '⚠️ Nada que crear (todo duplicado)';
      if (parts.length > 0) {
        toast.success(msg);
      } else {
        toast.info(msg);
      }
      setReviewDialog(null);
      if (parts.length > 0 && typeof onUpdate === 'function') onUpdate();
    } catch (e) {
      toast.error(`Error: ${e.message || e}`);
    } finally {
      setConfirming(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 10MB.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(
        `${API}/playlists/${playlist.id}/documentos/upload?tipo=${tipoDocumento}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      if (!response.ok) throw new Error('Error al subir archivo');

      const data = await response.json();
      
      // Update playlist with new document
      onUpdate({
        ...playlist,
        documentos: [...(playlist.documentos || []), data.documento]
      });

      setSelectedFile(null);
      setTipoDocumento('otro');
      
      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error:', error);
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentoId) => {
    if (!confirm('¿Eliminar este documento?')) return;

    try {
      const response = await fetch(
        `${API}/playlists/${playlist.id}/documentos/${documentoId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Error al eliminar');

      // Update playlist
      onUpdate({
        ...playlist,
        documentos: playlist.documentos.filter(d => d.id !== documentoId)
      });

    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el documento');
    }
  };

  const getTipoLabel = (tipo) => {
    const tipoObj = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    return tipoObj ? tipoObj.label : '📎 Documento';
  };

  // Descarga con auth header → blob URL. Los <a href> directos fallan porque
  // el navegador no envía el Bearer token al abrir en nueva pestaña.
  const fetchAsBlob = async (docId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/documentos/${docId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.text().catch(() => 'Error desconocido');
      throw new Error(`Error ${res.status}: ${err.slice(0, 120)}`);
    }
    return await res.blob();
  };

  const handleView = async (doc) => {
    // Determinar tipo (PDF, imagen, otro)
    const name = (doc.nombre || doc.filename || '').toLowerCase();
    const ct = (doc.content_type || '').toLowerCase();
    let kind = 'other';
    if (name.endsWith('.pdf') || ct.includes('pdf')) kind = 'pdf';
    else if (/\.(jpg|jpeg|png|gif|webp)$/i.test(name) || ct.startsWith('image/')) kind = 'image';

    // Para 'other' (no PDF/imagen) delegamos a descarga directa
    if (kind === 'other') {
      return handleDownloadFile(doc);
    }

    setPreviewLoading(true);
    try {
      const blob = await fetchAsBlob(doc.id);
      const blobUrl = URL.createObjectURL(blob);
      setPreviewDoc({
        id: doc.id,
        nombre: doc.nombre || doc.filename || `documento_${doc.id}`,
        kind,
        blobUrl,
      });
    } catch (e) {
      console.error('Error visualizando documento:', e);
      alert(`No se pudo abrir el documento: ${e.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewDoc?.blobUrl) {
      URL.revokeObjectURL(previewDoc.blobUrl);
    }
    setPreviewDoc(null);
  };

  const openPreviewInNewTab = () => {
    if (!previewDoc?.blobUrl) return;
    const a = document.createElement('a');
    a.href = previewDoc.blobUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownloadFile = async (doc) => {
    try {
      const blob = await fetchAsBlob(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nombre || doc.filename || `documento_${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Error descargando documento:', e);
      alert(`No se pudo descargar: ${e.message}`);
    }
  };

  const getFileIcon = (filename) => {
    const safeName = filename || '';
    const parts = safeName.split('.');
    const ext = (parts.length > 1 ? parts.pop() : '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <FileImage className="w-5 h-5 text-blue-400" />;
    } else if (['pdf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-400" />;
    }
    return <File className="w-5 h-5 text-slate-400" />;
  };

  const documentos = playlist.documentos || [];

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg text-white flex items-center justify-between gap-2 flex-wrap">
          <span>📁 Documentos del Evento</span>
          <div className="flex items-center gap-2">
            {documentos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReanalyzeAll}
                disabled={reanalyzing}
                className="bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20 text-xs h-8"
                data-testid="reanalyze-all-documents-btn"
              >
                {reanalyzing ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Re-analizando...</>
                ) : (
                  <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-analizar horarios (IA)</>
                )}
              </Button>
            )}
            <span className="text-sm px-3 py-1 rounded bg-slate-700">
              {documentos.length} archivo{documentos.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 space-y-3">
          <Label className="text-white">Subir Nuevo Documento</Label>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-slate-400">Tipo de Documento</Label>
              <select
                value={tipoDocumento}
                onChange={(e) => setTipoDocumento(e.target.value)}
                className="w-full bg-slate-800 border-slate-600 text-white rounded px-3 py-2 text-sm"
              >
                {TIPOS_DOCUMENTO.map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-sm text-slate-400">Archivo (Máx 10MB)</Label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                className="w-full bg-slate-800 border-slate-600 text-white rounded px-3 py-2 text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-slate-700 file:text-white hover:file:bg-slate-600"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx"
              />
            </div>
          </div>

          {selectedFile && (
            <div className="flex items-center gap-2 p-2 bg-slate-800 rounded">
              <File className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-white flex-1">{selectedFile.name}</span>
              <span className="text-xs text-slate-400">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </span>
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Subiendo...' : 'Subir Documento'}
          </Button>
        </div>

        {/* Documents List */}
        {documentos.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay documentos subidos</p>
            <p className="text-xs mt-1">Sube contratos, logos, referencias, etc.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documentos.map((doc) => {
              // Defensive: soporta ambos esquemas (nombre/filename, fecha_subida/uploaded_at, subido_por/uploaded_by)
              const docName = doc.nombre || doc.filename || 'documento';
              const docDate = doc.fecha_subida || doc.uploaded_at;
              const docUser = doc.subido_por || doc.uploaded_by;
              const docUrl = doc.url || `/documentos/${doc.id}/download`;
              return (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
              >
                {getFileIcon(docName)}
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">
                    {docName}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                    <span>{getTipoLabel(doc.tipo)}</span>
                    {docDate && (
                      <>
                        <span>•</span>
                        <span>{new Date(docDate).toLocaleDateString('es-ES')}</span>
                      </>
                    )}
                    {docUser && (
                      <>
                        <span>•</span>
                        <span>👤 {docUser}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {isExtractable(doc) && (
                    <button
                      onClick={() => handleExtractLinks(doc)}
                      disabled={extractingId === doc.id}
                      className="p-2 rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                      title="Analizar con IA y extraer canciones/momentos como peticiones (PDF, DOCX, DOC o imagen)"
                      data-testid={`doc-extract-links-btn-${doc.id}`}
                    >
                      <Music className={`w-4 h-4 ${extractingId === doc.id ? 'text-slate-400 animate-pulse' : 'text-purple-400'}`} />
                    </button>
                  )}
                  <button
                    onClick={() => handleView(doc)}
                    disabled={previewLoading}
                    className="p-2 rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                    title="Ver documento"
                    data-testid={`doc-view-btn-${doc.id}`}
                  >
                    <Eye className="w-4 h-4 text-cyan-400" />
                  </button>
                  
                  <button
                    onClick={() => handleDownloadFile(doc)}
                    className="p-2 rounded hover:bg-slate-800 transition-colors"
                    title="Descargar archivo"
                    data-testid={`doc-download-btn-${doc.id}`}
                  >
                    <Download className="w-4 h-4 text-green-400" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 rounded hover:bg-slate-800 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Preview modal inline: PDF (<iframe>) + imágenes (<img>) */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent
          className="bg-slate-900 text-white border-slate-700 max-w-5xl w-[95vw] h-[92vh] p-0 overflow-hidden flex flex-col"
          data-testid="doc-preview-modal"
        >
          <DialogHeader className="shrink-0 px-4 py-3 border-b border-slate-700 flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base min-w-0">
              {previewDoc?.kind === 'pdf' ? (
                <FileText className="w-5 h-5 text-red-400 shrink-0" />
              ) : (
                <FileImage className="w-5 h-5 text-blue-400 shrink-0" />
              )}
              <span className="truncate">{previewDoc?.nombre}</span>
            </DialogTitle>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={openPreviewInNewTab}
                className="p-2 rounded hover:bg-slate-800 text-slate-300"
                title="Abrir en nueva pestaña"
                data-testid="doc-preview-newtab-btn"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              <button
                onClick={() => previewDoc && handleDownloadFile({ id: previewDoc.id, nombre: previewDoc.nombre })}
                className="p-2 rounded hover:bg-slate-800 text-green-400"
                title="Descargar"
                data-testid="doc-preview-download-btn"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={closePreview}
                className="p-2 rounded hover:bg-slate-800 text-slate-300"
                title="Cerrar"
                data-testid="doc-preview-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto bg-slate-950">
            {previewDoc?.kind === 'pdf' && (
              <iframe
                src={previewDoc.blobUrl}
                title={previewDoc.nombre}
                className="w-full h-full border-0"
                data-testid="doc-preview-pdf-iframe"
              />
            )}
            {previewDoc?.kind === 'image' && (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={previewDoc.blobUrl}
                  alt={previewDoc.nombre}
                  className="max-w-full max-h-full object-contain"
                  data-testid="doc-preview-image"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Iter89.12: Modal de revisión de canciones extraídas */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => { if (!open && !confirming) setReviewDialog(null); }}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[88vh] p-0 gap-0 flex flex-col"
          data-testid="extract-review-dialog"
        >
          <div className="p-6 pb-3 shrink-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                {reviewDialog?.doc ? 'Revisar canciones detectadas' : 'Revisar re-análisis de horarios y observaciones'}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {reviewDialog?.doc ? (reviewDialog?.doc?.nombre || reviewDialog?.doc?.filename) : 'Todos los documentos y notas del evento'}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 space-y-3">
          {reviewDialog && (
            <>
              {reviewDialog.candidates.length > 0 && (
                <>
              {/* Resumen (Iter99: cuenta separada peticiones vs lista personalizada) */}
              {(() => {
                const counts = reviewDialog.candidates.reduce((acc, c) => {
                  const td = c.tipo_destino || 'peticion';
                  if (td === 'lista_personalizada') acc.lista += 1;
                  else acc.peticiones += 1;
                  if (reviewDialog.selected.has(c.id)) {
                    if (td === 'lista_personalizada') acc.selectedLista += 1;
                    else acc.selectedPeticiones += 1;
                  }
                  return acc;
                }, { peticiones: 0, lista: 0, selectedPeticiones: 0, selectedLista: 0 });
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="bg-slate-800/50 border border-slate-700 rounded p-2">
                      <div className="text-slate-500">🎯 Peticiones</div>
                      <div className="text-amber-300 font-bold text-lg">{counts.peticiones}</div>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700 rounded p-2">
                      <div className="text-slate-500">📋 Lista personalizada</div>
                      <div className="text-purple-300 font-bold text-lg">{counts.lista}</div>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700 rounded p-2">
                      <div className="text-slate-500">✅ Match Spotify</div>
                      <div className="text-emerald-300 font-bold text-lg">{reviewDialog.summary.llm_matched_in_spotify || 0}</div>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700 rounded p-2">
                      <div className="text-slate-500">📌 Sel. total</div>
                      <div className="text-cyan-300 font-bold text-lg" data-testid="extract-review-selected-count">
                        {reviewDialog.selected.size}/{reviewDialog.candidates.length}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Tabs: Peticiones vs Lista personalizada (Iter99) */}
              <div className="flex gap-2 border-b border-slate-700 mt-2">
                {[
                  { key: 'peticion', label: '🎯 Peticiones (con momento)', color: 'amber' },
                  { key: 'lista_personalizada', label: '📋 Lista personalizada', color: 'purple' },
                ].map(t => {
                  const count = reviewDialog.candidates.filter(c => (c.tipo_destino || 'peticion') === t.key || (t.key === 'peticion' && !c.tipo_destino)).length;
                  const isActive = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
                        isActive
                          ? `text-${t.color}-300 border-${t.color}-400`
                          : 'text-slate-400 border-transparent hover:text-slate-200'
                      }`}
                      data-testid={`extract-review-tab-${t.key}`}
                    >
                      {t.label} <span className="ml-1 text-[10px] opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>

              {/* Toggle all + hint */}
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={reviewDialog.selected.size === reviewDialog.candidates.length}
                    onCheckedChange={() => toggleAllCandidates(reviewDialog.selected.size === reviewDialog.candidates.length)}
                    data-testid="extract-review-toggle-all"
                  />
                  <span className="text-xs text-slate-400">
                    {reviewDialog.selected.size === reviewDialog.candidates.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </span>
                </div>
                <span className="text-[10px] text-slate-500 italic">
                  💡 Usa ↔ para mover entre pestañas
                </span>
              </div>
              
              {/* Lista candidatas filtradas por tab activo */}
              <div className="pr-1">
                <div className="space-y-1">
                  {reviewDialog.candidates
                    .filter(c => (c.tipo_destino || 'peticion') === activeTab)
                    .map(c => {
                    const isSelected = reviewDialog.selected.has(c.id);
                    const isLLM = c.source === 'doc_extract_llm';
                    return (
                      <div
                        key={c.id}
                        className={`flex items-start gap-2 p-2 rounded border transition-colors ${
                          isSelected
                            ? 'bg-cyan-950/30 border-cyan-500/40'
                            : 'bg-slate-800/30 border-slate-700/50 opacity-60'
                        }`}
                        data-testid={`extract-review-item-${c.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleCandidate(c.id)}
                          className="mt-2"
                          data-testid={`extract-review-checkbox-${c.id}`}
                        />
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isLLM ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded font-bold flex-shrink-0">
                                🤖 LLM
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-bold flex-shrink-0">
                                <Link2 className="w-3 h-3 inline" /> URL
                              </span>
                            )}
                            <input
                              type="text"
                              value={c.titulo || ''}
                              onChange={(e) => updateCandidateField(c.id, 'titulo', e.target.value)}
                              placeholder="Título"
                              className="flex-1 min-w-0 bg-transparent text-white font-medium text-sm border-b border-transparent hover:border-slate-600 focus:border-cyan-500 focus:outline-none px-0.5 py-0.5"
                              data-testid={`extract-review-titulo-${c.id}`}
                            />
                            <button
                              type="button"
                              onClick={() => reSearchSpotify(c)}
                              disabled={searchingId === c.id || !(c.titulo || '').trim()}
                              className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Re-buscar en Spotify con los valores actuales"
                              data-testid={`extract-review-research-${c.id}`}
                            >
                              {searchingId === c.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Search className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => swapCandidateDestino(c.id)}
                              className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-purple-300 hover:bg-purple-900/20"
                              title={`Mover a ${(c.tipo_destino || 'peticion') === 'lista_personalizada' ? 'Peticiones' : 'Lista personalizada'}`}
                              data-testid={`extract-review-swap-${c.id}`}
                            >
                              <span className="text-base leading-none" aria-hidden="true">↔</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-2 pl-1">
                            <span className="text-slate-500 text-[10px] uppercase tracking-wide w-14 flex-shrink-0">Artista</span>
                            <input
                              type="text"
                              value={c.artista || ''}
                              onChange={(e) => updateCandidateField(c.id, 'artista', e.target.value)}
                              placeholder="Artista (opcional)"
                              className="flex-1 min-w-0 bg-transparent text-slate-300 text-sm border-b border-transparent hover:border-slate-600 focus:border-cyan-500 focus:outline-none px-0.5 py-0.5"
                              data-testid={`extract-review-artista-${c.id}`}
                            />
                          </div>
                          {(c.tipo_destino || 'peticion') === 'peticion' ? (
                            <div className="flex items-center gap-2 pl-1">
                              <span className="text-amber-300/70 text-[10px] uppercase tracking-wide w-14 flex-shrink-0">📍 Mom.</span>
                              <input
                                type="text"
                                value={c.momento || ''}
                                onChange={(e) => updateCandidateField(c.id, 'momento', e.target.value)}
                                placeholder="Momento (ej. Corte tarta)"
                                className="flex-1 min-w-0 bg-transparent text-amber-300 text-xs border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5 py-0.5"
                                data-testid={`extract-review-momento-${c.id}`}
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 pl-1">
                              <span className="text-purple-300/70 text-[10px] uppercase tracking-wide w-14 flex-shrink-0">📁 Cat.</span>
                              <input
                                type="text"
                                value={c.categoria || ''}
                                onChange={(e) => updateCandidateField(c.id, 'categoria', e.target.value)}
                                placeholder="Categoría (ej. Reggaeton clásico)"
                                className="flex-1 min-w-0 bg-transparent text-purple-300 text-xs border-b border-transparent hover:border-slate-600 focus:border-purple-500 focus:outline-none px-0.5 py-0.5"
                                data-testid={`extract-review-categoria-${c.id}`}
                              />
                            </div>
                          )}
                          {c.url && (
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 mt-0.5 font-mono truncate max-w-full pl-1"
                            >
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{c.url}</span>
                            </a>
                          )}
                          
                          {/* Iter89.15: Panel de top-5 opciones de Spotify para casos ambiguos */}
                          {spotifyOptions[c.id] && spotifyOptions[c.id].length > 0 && (
                            <div className="mt-2 ml-1 bg-slate-950/80 border border-emerald-500/40 rounded p-2 space-y-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-emerald-300 uppercase tracking-wide font-bold">
                                  🎯 Elige el match correcto:
                                </span>
                                <button
                                  type="button"
                                  onClick={() => closeSpotifyOptions(c.id)}
                                  className="text-slate-500 hover:text-slate-300 p-0.5"
                                  title="Cerrar"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              {spotifyOptions[c.id].map((track, idx) => {
                                const isCurrent = c.spotify_url === track.spotify_url;
                                const cover = track.album?.images?.[track.album.images.length - 1]?.url
                                  || track.album?.images?.[0]?.url;
                                const artistsName = (track.artists || []).map(a => a.name).join(', ');
                                return (
                                  <button
                                    key={track.id || idx}
                                    type="button"
                                    onClick={() => applySpotifyOption(c.id, track)}
                                    className={`w-full flex items-center gap-2 p-1.5 rounded text-left transition-colors ${
                                      isCurrent
                                        ? 'bg-emerald-900/30 border border-emerald-500/50'
                                        : 'bg-slate-900/60 border border-slate-700 hover:border-emerald-500/40 hover:bg-emerald-900/10'
                                    }`}
                                    data-testid={`extract-review-spotify-option-${c.id}-${idx}`}
                                  >
                                    {cover ? (
                                      <img
                                        src={cover}
                                        alt=""
                                        className="w-8 h-8 rounded flex-shrink-0 object-cover"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded bg-slate-800 flex-shrink-0 flex items-center justify-center">
                                        <Music className="w-4 h-4 text-slate-600" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-white font-medium truncate">
                                        {track.name}
                                      </div>
                                      <div className="text-[10px] text-slate-400 truncate">
                                        {artistsName} · <span className="text-slate-500">{track.album?.name || ''}</span>
                                      </div>
                                    </div>
                                    {isCurrent && (
                                      <span className="text-[9px] px-1 py-0.5 bg-emerald-500/30 text-emerald-300 rounded flex-shrink-0">
                                        ACTUAL
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </>
              )}

              {/* Horarios y observaciones detectados (2026-10, Fase 1) + momentos extra/reunión (Fase 3) */}
              {(reviewDialog.horarios.length > 0 || reviewDialog.observaciones.length > 0 ||
                (reviewDialog.momentosExtra || []).length > 0 || reviewDialog.reunion?.detectada) && (
                <div className="border-t border-slate-700 pt-3 mt-2 space-y-3">
                  <ScheduleNotesReviewSections
                    horarios={reviewDialog.horarios}
                    selectedHorarios={reviewDialog.selectedHorarios}
                    onToggleHorario={toggleHorario}
                    observaciones={reviewDialog.observaciones}
                    selectedObservaciones={reviewDialog.selectedObservaciones}
                    onToggleObservacion={toggleObservacion}
                  />
                  <ExtraMomentsReviewSection
                    momentosExtra={reviewDialog.momentosExtra || []}
                    selectedExtra={reviewDialog.selectedMomentosExtra}
                    onToggleExtra={toggleMomentoExtra}
                  />
                  <ReunionDetectedCard
                    reunion={reviewDialog.reunion}
                    enabled={reviewDialog.reunionEnabled}
                    onToggleEnabled={setReunionEnabled}
                    fecha={reviewDialog.reunionFecha}
                    hora={reviewDialog.reunionHora}
                    onChangeFecha={setReunionFecha}
                    onChangeHora={setReunionHora}
                  />
                </div>
              )}
            </>
          )}
          </div>

          <div className="p-6 pt-3 border-t border-slate-700 shrink-0">
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewDialog(null)}
              disabled={confirming}
              className="bg-slate-800 border-slate-600 text-slate-300"
              data-testid="extract-review-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmCreatePeticiones}
              disabled={confirming || !reviewDialog || (
                reviewDialog.selected.size === 0 &&
                reviewDialog.selectedHorarios.size === 0 &&
                reviewDialog.selectedObservaciones.size === 0 &&
                (reviewDialog.selectedMomentosExtra?.size || 0) === 0 &&
                !(reviewDialog.reunionEnabled && reviewDialog.reunion?.detectada)
              )}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="extract-review-confirm"
            >
              {confirming ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (() => {
                if (!reviewDialog) return 'Guardar';
                const sel = reviewDialog.candidates.filter(c => reviewDialog.selected.has(c.id));
                const nPet = sel.filter(c => (c.tipo_destino || 'peticion') !== 'lista_personalizada').length;
                const nLista = sel.filter(c => c.tipo_destino === 'lista_personalizada').length;
                const nHorarios = reviewDialog.selectedHorarios.size + (reviewDialog.selectedMomentosExtra?.size || 0);
                const nObs = reviewDialog.selectedObservaciones.size;
                const parts = [];
                if (nPet > 0) parts.push(`${nPet} peticiones`);
                if (nLista > 0) parts.push(`${nLista} a lista`);
                if (nHorarios > 0) parts.push(`${nHorarios} horarios`);
                if (nObs > 0) parts.push(`${nObs} observaciones`);
                if (reviewDialog.reunionEnabled && reviewDialog.reunion?.detectada) parts.push('reunión');
                return parts.length > 0 ? `Guardar ${parts.join(' + ')}` : 'Guardar';
              })()}
            </Button>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
